import { z } from 'zod';

/** A flag do feature-flag-service (`currentFeatures(system: USER_APP)`) que a corrida com a
 *  99 lê: só o atalho de ajuda dentro da corrida. A `mobility_99` não libera mais a corrida
 *  (#7330). */
export const MOBILITY_99_FEATURE_FLAGS = ['mobility_99_help_center'] as const;

export interface Mobility99AccessInput {
  /** `policyAccess` contém `mobility`; `null` = não resolvido. */
  mobilityPolicy: boolean | null;
  /** `policyGetAccessModal.smartMobility`; `null` = não resolvido. */
  smartMobility: boolean | null;
  /** `currentFeatures(USER_APP)` filtradas; `null` = não resolvido. */
  featureFlags: readonly string[] | null;
}

// `unavailable` só acompanha `offer: false`: uma política que decide não foi lida e o que se SABE
// aponta para a 99 — a tela mantém a Mobilidade com o aviso e relê em vez de sumir com ela
// (#7318), como a carteira (#6450).
export const Mobility99EntrySchema = z
  .object({ offer: z.boolean(), helpCenter: z.boolean(), unavailable: z.literal(true).optional() })
  .strict();
export type Mobility99Entry = z.infer<typeof Mobility99EntrySchema>;

/** Por que a 99 não foi oferecida — o motivo do log do `/me` (#7318). `no_*` é uma política que
 *  respondeu "não"; `unresolved_*`, a primeira que não respondeu. */
export type Mobility99HiddenReason =
  | 'no_policy'
  | 'no_smart_mobility'
  | 'unresolved_policy'
  | 'unresolved_smart_mobility';

export interface Mobility99Verdict {
  entry: Mobility99Entry;
  /** Ausente quando oferece. */
  reason?: Mobility99HiddenReason;
}

const HIDDEN: Mobility99Entry = { offer: false, helpCenter: false };
const UNAVAILABLE: Mobility99Entry = { offer: false, helpCenter: false, unavailable: true };

/**
 * Se a corrida com a 99 aparece: a barra "CORRIDAS" da home nova do app
 * (`get_mobility_99_home_banner_state.dart`), sem o Smart Mobility (spec §4).
 * Só as políticas decidem: sem a política `mobility` ou sem `smartMobility` ("um checkbox
 * governa a barra", commit `6769d7369` do app), esconde. A flag legada `mobility_99` não decide
 * mais nada (#7330); as flags só ligam o atalho de ajuda, e não resolvidas o desligam sem
 * esconder a corrida. Gastos bloqueados NÃO entram, como no legado. Canal (app nativo) e a
 * capacidade da ponte ficam no cliente.
 *
 * Política não resolvida NUNCA oferece. `unavailable` (#7318): só quando nenhuma política
 * resolvida descarta a 99 e um sinal DA 99 aponta para ela — a `smartMobility` ou o atalho de
 * ajuda da 99. A política `mobility` sozinha não serve: ela também libera o Agendamento, e a
 * empresa só com ele veria "não deu para verificar a 99". Sem sinal da 99, ela só some (o cliente
 * ainda lembra de quem já a via na sessão).
 */
export function mobility99Verdict(input: Mobility99AccessInput): Mobility99Verdict {
  const { mobilityPolicy, smartMobility, featureFlags } = input;
  const helpCenter = featureFlags?.includes('mobility_99_help_center') === true;
  if (mobilityPolicy === false) return { entry: HIDDEN, reason: 'no_policy' };
  if (smartMobility === false) return { entry: HIDDEN, reason: 'no_smart_mobility' };
  if (mobilityPolicy === null || smartMobility === null) {
    const reason = mobilityPolicy === null ? 'unresolved_policy' : 'unresolved_smart_mobility';
    const pointsTo = smartMobility === true || helpCenter;
    return { entry: pointsTo ? UNAVAILABLE : HIDDEN, reason };
  }
  return { entry: { offer: true, helpCenter } };
}

export function mobility99Entry(input: Mobility99AccessInput): Mobility99Entry {
  return mobility99Verdict(input).entry;
}
